import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { InlineCheckboxListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { Person } from "@/Common/Models/Person/Person";
import { RelationsHelper } from "@/Patient/Helpers/RelationsHelper";
import { RelationInfo } from "./RelationInfo";
import { RelativeIcon } from "./RelativeIcon";

import styles from "./RelationEdit.module.scss";

interface IRelationEditProps {
    index: number;
    linkedContact: LinkedContact;
    onRemoveRelation: () => void;
    onChanged: (mutation: (linkedContact: LinkedContact) => void) => void;
}

function RelationEdit(props: IRelationEditProps): JSX.Element {
    const { getText } = useTranslation();

    const currentPerson = sessionStore.selectedFile;
    const person: Person = Person.FromLinkedContact(props.linkedContact);

    const representativeTypes = cacheStore.GetRAMQTypeRepresentant(true);

    const displayPermissionSection = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2
        && (person.ContactFacetType === ContactFacetType.Patient
            || currentPerson.ContactFacetType === ContactFacetType.Patient);

    const relationshipsLookupOptions: JSX.Element[] = cacheStore.GetRelationshipTypeLookups()
        .map((p: FlatLookupView) => {
            const longValue = cacheStore.GetLocalizedValueByLookupId(p.LookupID);
            return <option key={p.LookupID} value={p.LookupID} title={longValue}>{longValue}</option>;
        });

    const onChangeLinkedContact = (mutation: (lc: LinkedContact) => void): void => {
        props?.onChanged(mutation);
    };

    const onSelectRelationshipType = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const relationshipTypeLookupId = e.target.value;
        onChangeLinkedContact(p => p.RelationLookupId = relationshipTypeLookupId);
    };

    const onSelectRepresentativeType = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const representativeType = e.target.value;
        onChangeLinkedContact(p => p.RepresentativeType = representativeType);
    };

    return (
        <div className={styles.relationView} data-cy="relationEdit">
            <RelativeIcon person={person} />
            <div className={styles.relationViewInfo}>
                <RelationInfo person={person} />
                {currentPerson.ContactFacetType === ContactFacetType.Patient && (
                    <div className={styles.infoBodyRow}>
                        <CheckboxControl
                            checked={props.linkedContact.IsRepresentative}
                            label={getText("RepresentativeLabel")}
                            onChange={isChecked => onChangeLinkedContact(linkedContact => {
                                linkedContact.IsRepresentative = isChecked;
                                if (isChecked && !linkedContact.RepresentativeType)
                                    linkedContact.RepresentativeType = representativeTypes?.firstOrDefault()[0];
                            })}
                        />
                        <CheckboxControl
                            checked={props.linkedContact.IsEmergencyContact}
                            label={getText("EmergencyContactLabel")}
                            onChange={isChecked => onChangeLinkedContact(linkedContact => linkedContact.IsEmergencyContact = isChecked)}
                            labelClassName={styles.editSelection}
                        />
                    </div>
                )}
                <div className={styles.relationshipSection}>
                    <div className={styles.infoBodyLeft}>
                        <label htmlFor={`relationEdit_${props.index}_relationType`} className="bold">{getText("RelationshipLabel")}</label>
                        <select
                            id={`relationEdit_${props.index}_relationType`}
                            key={`${props.index}_relationType`}
                            className="form-control myle-control"
                            onChange={e => onSelectRelationshipType(e)}
                            value={props.linkedContact.RelationLookupId}
                        >
                            {relationshipsLookupOptions}
                        </select>
                    </div>
                    {props.linkedContact.IsRepresentative && (
                        <div className={styles.representativeTypeSection}>
                            <label className="bold">{getText("TypeLabel")}</label>
                            <div>
                                <select
                                    className="form-control myle-control"
                                    value={props.linkedContact.RepresentativeType}
                                    onChange={(e: any) => onSelectRepresentativeType(e)}
                                >
                                    {representativeTypes.map((type, idx) =>
                                        <option key={`${type[0]}_${idx}`} value={type[0]}>{type[1]}</option>
                                    )}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
                {displayPermissionSection && (
                    <div className={styles.portalAccess}>
                        <h4 className={`thin-font ${styles.portalTitle}`}>{getText("PortalAccessRightsLabel")}</h4>
                        {person.ContactFacetType === ContactFacetType.Patient && (
                            <>
                                <span>
                                    <strong>{person.FirstName}</strong>
                                    {` ${getText("SharesWithLabel")} `}
                                    <strong>{currentPerson.FirstName}</strong>:
                                </span>
                                <InlineCheckboxListControl
                                    id="relationPortalAccessEdit"
                                    items={RelationsHelper.getPermissionsOptions()}
                                    values={RelationsHelper.getPermissionsArray(props.linkedContact.ReversePermissionFlags)}
                                    onChange={permissions => onChangeLinkedContact(linkedContact => linkedContact.ReversePermissionFlags = RelationsHelper.mergeFlags(permissions))}
                                />
                            </>
                        )}
                        {currentPerson.ContactFacetType === ContactFacetType.Patient && (
                            <>
                                <span>
                                    <strong>{currentPerson.FirstName}</strong>
                                    {` ${getText("SharesWithLabel")} `}
                                    <strong>{person.FirstName}</strong>:
                                </span>
                                <InlineCheckboxListControl
                                    id="relatedRelationPortalAccessEdit"
                                    items={RelationsHelper.getPermissionsOptions()}
                                    values={RelationsHelper.getPermissionsArray(props.linkedContact.PermissionFlags)}
                                    onChange={permissions => onChangeLinkedContact(linkedContact => linkedContact.PermissionFlags = RelationsHelper.mergeFlags(permissions))}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
            <div data-cy="relationEdit-remove">
                <RemoveButton
                    onRemove={() => props.onRemoveRelation()}
                />
            </div>
        </div>
    );
}

export { RelationEdit };
